import React, { useState, useEffect, useContext } from 'react';
import { Card, Table, Button, Badge, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { FaCheck, FaTimes, FaEye, FaClock, FaBuilding, FaUser, FaEnvelope, FaPhone } from 'react-icons/fa';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const PendingSignups = () => {
  const [pendingSignups, setPendingSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSignup, setSelectedSignup] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const { token } = useContext(AuthContext);
  
  const fetchPendingSignups = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/onboarding/pending', {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch pending signups');
      }
      
      const data = await response.json();
      setPendingSignups(data);
    } catch (err) {
      console.error('Error fetching pending signups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPendingSignups();
  }, [token]);
  
  const handleApprove = async (signup) => {
    if (!window.confirm(`Are you sure you want to approve "${signup.CompanyName}"? This will:\n\n• Create their database\n• Set up their user account\n• Send them a welcome email with login credentials`)) {
      return;
    }
    
    setProcessing(true);
    
    try {
      const response = await fetch(`/api/onboarding/${signup.CustomerId}/approve`, {
        method: 'POST',
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.msg || 'Failed to approve signup');
      }
      
      toast.success(`✅ ${signup.CompanyName} has been approved! Welcome email sent.`);
      fetchPendingSignups();
      setShowDetailModal(false);
    } catch (err) {
      console.error('Error approving signup:', err);
      toast.error(err.message || 'Failed to approve signup');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async () => {
    if (!selectedSignup) return;
    
    setProcessing(true);
    
    try {
      const response = await fetch(`/api/onboarding/${selectedSignup.CustomerId}/reject`, {
        method: 'POST',
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectReason })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.msg || 'Failed to reject signup');
      }
      
      toast.info(`Signup for "${selectedSignup.CompanyName}" has been rejected.`);
      fetchPendingSignups();
      setShowRejectModal(false);
      setShowDetailModal(false);
      setRejectReason('');
    } catch (err) {
      console.error('Error rejecting signup:', err);
      toast.error(err.message || 'Failed to reject signup');
    } finally {
      setProcessing(false);
    }
  };
  
  const openRejectModal = (signup) => {
    setSelectedSignup(signup);
    setShowRejectModal(true);
  };
  
  const openDetailModal = (signup) => {
    setSelectedSignup(signup);
    setShowDetailModal(true);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getLicenseTypeBadge = (type) => {
    const variants = {
      'TRIAL': 'info',
      'STANDARD': 'success',
      'HALFYEAR': 'primary',
      'YEARLY': 'warning'
    };
    return <Badge bg={variants[type] || 'secondary'}>{type}</Badge>;
  };
  
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading pending signups...</p>
      </div>
    );
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <FaClock className="me-2 text-warning" />
            Pending Signups
          </h2>
          <p className="text-muted mb-0">
            Review and approve new customer signup requests
          </p>
        </div>
        <Button variant="outline-primary" onClick={fetchPendingSignups}>
          Refresh
        </Button>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {pendingSignups.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <FaCheck size={50} className="text-success mb-3" />
            <h4>All Caught Up!</h4>
            <p className="text-muted">There are no pending signup requests at this time.</p>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header className="bg-warning text-dark">
            <strong>{pendingSignups.length}</strong> pending signup{pendingSignups.length !== 1 ? 's' : ''} awaiting review
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>License Type</th>
                  <th>Submitted</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingSignups.map(signup => (
                  <tr key={signup.CustomerId}>
                    <td>
                      <strong>{signup.CompanyName}</strong>
                      <br />
                      <small className="text-muted">{signup.City}, {signup.State}</small>
                    </td>
                    <td>{signup.OwnerName}</td>
                    <td>
                      <a href={`mailto:${signup.Email}`}>{signup.Email}</a>
                    </td>
                    <td>{getLicenseTypeBadge(signup.PendingLicenseType)}</td>
                    <td>
                      <small>{formatDate(signup.CreatedAt)}</small>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-1"
                        onClick={() => openDetailModal(signup)}
                        title="View Details"
                      >
                        <FaEye />
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        className="me-1"
                        onClick={() => handleApprove(signup)}
                        disabled={processing}
                        title="Approve"
                      >
                        <FaCheck />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => openRejectModal(signup)}
                        disabled={processing}
                        title="Reject"
                      >
                        <FaTimes />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
      
      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <FaBuilding className="me-2" />
            Signup Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSignup && (
            <div>
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5 className="border-bottom pb-2">
                    <FaBuilding className="me-2 text-primary" />
                    Company Information
                  </h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td className="fw-bold" style={{ width: '40%' }}>Company Name:</td>
                        <td>{selectedSignup.CompanyName}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Address:</td>
                        <td>
                          {selectedSignup.Address}<br />
                          {selectedSignup.City}, {selectedSignup.State} {selectedSignup.ZipCode}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h5 className="border-bottom pb-2">
                    <FaUser className="me-2 text-primary" />
                    Contact Information
                  </h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td className="fw-bold" style={{ width: '40%' }}>Owner/Contact:</td>
                        <td>{selectedSignup.OwnerName}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold"><FaEnvelope className="me-1" />Email:</td>
                        <td><a href={`mailto:${selectedSignup.Email}`}>{selectedSignup.Email}</a></td>
                      </tr>
                      <tr>
                        <td className="fw-bold"><FaPhone className="me-1" />Phone:</td>
                        <td>{selectedSignup.Phone}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5 className="border-bottom pb-2">Account Details</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td className="fw-bold" style={{ width: '40%' }}>License Type:</td>
                        <td>{getLicenseTypeBadge(selectedSignup.PendingLicenseType)}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Username:</td>
                        <td><code>{selectedSignup.PendingUsername}</code></td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Submitted:</td>
                        <td>{formatDate(selectedSignup.CreatedAt)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h5 className="border-bottom pb-2">Upon Approval</h5>
                  <ul className="list-unstyled">
                    <li>✓ Customer database will be created</li>
                    <li>✓ Admin user will be created</li>
                    <li>✓ License will be activated</li>
                    <li>✓ Welcome email will be sent</li>
                  </ul>
                </div>
              </div>
              
              {selectedSignup.SignupNotes && (
                <div className="mb-4">
                  <h5 className="border-bottom pb-2">Customer Notes</h5>
                  <div className="bg-light p-3 rounded">
                    {selectedSignup.SignupNotes}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          <Button 
            variant="danger" 
            onClick={() => openRejectModal(selectedSignup)}
            disabled={processing}
          >
            <FaTimes className="me-1" /> Reject
          </Button>
          <Button 
            variant="success" 
            onClick={() => handleApprove(selectedSignup)}
            disabled={processing}
          >
            {processing ? (
              <><Spinner animation="border" size="sm" className="me-1" /> Processing...</>
            ) : (
              <><FaCheck className="me-1" /> Approve & Send Welcome Email</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>Reject Signup</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSignup && (
            <>
              <p>
                Are you sure you want to reject the signup for <strong>{selectedSignup.CompanyName}</strong>?
              </p>
              <Form.Group>
                <Form.Label>Reason for rejection (optional):</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleReject}
            disabled={processing}
          >
            {processing ? (
              <><Spinner animation="border" size="sm" className="me-1" /> Processing...</>
            ) : (
              'Reject Signup'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PendingSignups;
